import { RedirectionTypesEnum } from 'common/enums/ButtonActionEnum'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { BorderStyleEnum } from 'common/types/styleTypes'
import i18n, { TFunction } from 'i18next'
import { contactFieldLocoKeys } from 'src/shared/constants/contact-field-loco-keys'
import { defaultFieldSubEntities } from 'src/shared/entities/form/constants'
import {
  generateEntity,
  generateHtmlAttrId,
  generateId,
} from 'src/shared/entities/utils/generate-base-entity'
import { ContactSlugEnum } from 'src/shared/enums/contact-fields-enum'
import { TypedTFunctionInterface } from 'src/shared/i18next/types/typed-t-function-interface'
import { MasterBlockIdType } from 'src/shared/schemas/master-block-id.schema'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'
import { getEntityNameLocoKeyByEntityType } from 'src/shared/utils/get-entity-name-loco-key-by-entity-type'
import { FormLayoutEnum, FormSubEntityTypeEnum } from './enums'
import { FormInterface } from './form.schema'
import { FormCheckboxInterface } from './form-checkbox-sub-entity.schema'
import { FormFieldInterface } from './form-field-sub-entity.schema'
import { FormSubmitButtonInterface } from './form-submit-button-sub-entity.schema'

export const createFormFieldSubEntity = ({
  slug,
  t,
  placeholder,
}: {
  slug: ContactSlugEnum
  t: TypedTFunctionInterface
  placeholder?: string
}): FormFieldInterface => ({
  id: generateId(),
  type: FormSubEntityTypeEnum.Field,
  slug,
  htmlAttrId: generateHtmlAttrId(FormSubEntityTypeEnum.Field),
  placeholder:
    placeholder ||
    (contactFieldLocoKeys[slug] ? t(contactFieldLocoKeys[slug]) : ''),
  optional: false,
})

export const createFormCheckboxSubEntity = ({
  t,
}: {
  t: TypedTFunctionInterface
}): FormCheckboxInterface => ({
  id: generateId(),
  type: FormSubEntityTypeEnum.Checkbox,
  htmlAttrId: generateHtmlAttrId(FormSubEntityTypeEnum.Checkbox),
  content: t('entities.explicit_consent.default_text'),
  padding: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 10,
    paddingRight: 0,
  },
  mobilePadding: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 8,
    paddingRight: 0,
  },
  errorText: t('settings_options.checkbox_message'),
  optional: false,
})

export const createFormSubmitButtonSubEntity = ({
  t,
}: {
  t: TFunction
}): FormSubmitButtonInterface => ({
  id: generateId(),
  type: FormSubEntityTypeEnum.SubmitButton,
  htmlAttrId: generateHtmlAttrId(FormSubEntityTypeEnum.SubmitButton),
  text: t('entities.form.button.default_text'),
})

export const createForm = ({
  parentId,
  masterBlockId,
  locale,
  customName,
}: {
  parentId: string
  masterBlockId?: MasterBlockIdType
  locale: string
  customName?: string
}): FormInterface => {
  const t = i18n.getFixedT(locale)

  return {
    ...generateEntity(EntityTypeEnum.Form, parentId, masterBlockId),
    customName:
      customName || t(getEntityNameLocoKeyByEntityType(EntityTypeEnum.Form)),
    layout: FormLayoutEnum.OneColumn,
    rowGap: 12,
    mobileRowGap: 9,
    columnGap: 12,

    subEntities: [
      ...defaultFieldSubEntities.map(slug =>
        createFormFieldSubEntity({ slug, t }),
      ),
      createFormCheckboxSubEntity({ t }),
      createFormSubmitButtonSubEntity({ t }),
    ],
    fieldsCommonStyles: {},
    buttonCommonStyles: {
      buttonType: WebsiteSettingsTypographyTypeEnum.PrimaryButton,
      alignSelf: 'center',
    },
    checkboxesCommonStyles: {},
    action: {
      redirectionType: RedirectionTypesEnum.none,
      successOptInScreen: {
        headingText: t(
          'entity_settings.form.success_opt_in_screen.default_heading_text',
        ),
        text: t('entity_settings.form.success_opt_in_screen.default_text'),
        iconColor: 'rgb(38, 191, 86)',
      },
      doubleOptIn: false,
    },
    border: {
      width: 2,
      style: BorderStyleEnum.solid,
      color: 'rgb(226, 226, 227)',
      radius: 24,
    },
    padding: {
      paddingTop: 36,
      paddingBottom: 36,
      paddingLeft: 36,
      paddingRight: 36,
    },
    mobilePadding: {
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 10,
      paddingRight: 10,
    },
    margin: { marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 },
    backgroundColor: 'rgb(255, 255, 255)',
  }
}
